import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, get, onSessionLost, post, sessionStarted } from './api';
import { PORTAL_WAIT } from './portal/offline';
import { viewAsId } from './viewAs';

export interface User {
  id: number;
  email: string;
  displayName: string;
  capabilities: string[];
  /** Set while an office user is looking at the portal as this contractor. */
  viewedBy?: { userId: number; displayName: string };
}

interface AuthState {
  /** undefined while the first check is in flight. */
  user: User | null | undefined;
  signIn(business: string, email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

const KEY = (realm: string) => `teknika_${realm}_user`;
function remember(realm: string, user: User | null) {
  try {
    if (user) localStorage.setItem(KEY(realm), JSON.stringify(user));
    else localStorage.removeItem(KEY(realm));
  } catch {
    // Storage blocked: offline start-up just shows the sign-in screen.
  }
}
function recall(realm: string): User | null {
  try {
    const raw = localStorage.getItem(KEY(realm));
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({
  children,
  realm = 'staff',
}: {
  children: ReactNode;
  realm?: 'staff' | 'portal';
}) {
  // A contractor who signed in on this phone starts straight on their saved work while the
  // server is asked; if the session has gone, the answer below sends them to the sign-in.
  const [user, setUser] = useState<User | null | undefined>(() =>
    realm === 'portal' && viewAsId() === null ? (recall(realm) ?? undefined) : undefined,
  );

  useEffect(() => {
    // While viewing as someone, nothing about them is saved: not who they are, not for offline start-up.
    const viewing = viewAsId() !== null;
    get<{ user: User }>(`/api/auth/${realm}/me`, realm === 'portal' ? PORTAL_WAIT : undefined)
      .then((r) => {
        if (!viewing) remember(realm, r.user);
        setUser(r.user);
      })
      .catch((e) => {
        if (e instanceof ApiError) {
          if (!viewing) remember(realm, null);
          setUser(null);
        } else {
          // No signal, which is not the same as signed out: a contractor opening the app
          // in a basement should still see their saved work.
          setUser(realm === 'portal' && !viewing ? recall(realm) : null);
        }
      });
  }, [realm]);

  // Signed out elsewhere, or the session ran out: back to the sign-in, not a page of errors.
  useEffect(
    () =>
      onSessionLost(() => {
        if (viewAsId() === null) remember(realm, null);
        setUser(null);
      }),
    [realm],
  );

  const signIn = useCallback(
    async (business: string, email: string, password: string) => {
      const r = await post<{ user: User }>(`/api/auth/${realm}/login`, {
        business,
        email,
        password,
      });
      sessionStarted();
      remember(realm, r.user);
      setUser(r.user);
    },
    [realm],
  );
  const signOut = useCallback(async () => {
    try {
      await post(`/api/auth/${realm}/logout`);
    } catch (e) {
      // Signing out with no signal still signs out on this phone.
      if (e instanceof ApiError) throw e;
    }
    remember(realm, null);
    setUser(null);
  }, [realm]);

  return <Ctx.Provider value={{ user, signIn, signOut }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
